<template>
  <div class="da-page">
    <div class="page-header">
      <PageHeaderPortal>
        <div class="page-header-left">
          <div class="page-icon"><i class="fa-duotone fa-file-shield"></i></div>
          <div>
            <h1 class="page-title">Disclosure Audit</h1>
            <p class="page-subtitle">Initial LE packages checked against the Initial Disclosure List — results, rules and settings</p>
          </div>
        </div>
      </PageHeaderPortal>
      <div class="header-actions">
        <button class="btn" @click="refreshTab" :disabled="loading">{{ loading ? 'Loading...' : 'Refresh' }}</button>
      </div>
    </div>

    <div class="tabs">
      <button v-for="t in tabs" :key="t.key" class="tab" :class="{ active: tab === t.key }" @click="switchTab(t.key)">
        <i :class="t.icon"></i> {{ t.label }}
        <span v-if="t.key === 'suggestions' && suggestions.length" class="tab-badge">{{ suggestions.length }}</span>
      </button>
    </div>

    <div v-if="errorMessage" class="error-banner">{{ errorMessage }}</div>
    <div v-if="flash" class="flash-banner">{{ flash }}</div>

    <!-- ═══════════════════════ RESULTS ═══════════════════════ -->
    <section v-if="tab === 'results'">
      <div class="queue-status-bar" v-if="queueStatus">
        <div class="queue-status-label"><i class="fa-duotone fa-database"></i> Queue</div>
        <div class="queue-pill pending"><span class="q-count">{{ queueStatus.pending }}</span><span class="q-label">Pending</span></div>
        <div class="queue-pill processing"><span class="q-count">{{ queueStatus.processing }}</span><span class="q-label">Processing</span></div>
        <div class="queue-pill done"><span class="q-count">{{ queueStatus.done }}</span><span class="q-label">Done</span></div>
        <div class="queue-pill pending"><span class="q-count">{{ queueStatus.skipped }}</span><span class="q-label">Skipped</span></div>
        <div class="queue-pill errored"><span class="q-count">{{ queueStatus.error }}</span><span class="q-label">Error</span></div>
        <div class="spacer"></div>
        <span v-if="enabledFlag === false" class="pill warn">Processing is OFF (Settings → Enabled)</span>
      </div>

      <div class="toolbar">
        <input v-model="search" class="input grow" placeholder="Search loan #, borrower, LO, summary..." />
        <select v-model="resultFilters.decision" class="input" @change="loadResults">
          <option value="">All decisions</option>
          <option value="fail">Fail</option>
          <option value="review">Review</option>
          <option value="pass">Pass</option>
          <option value="error">Error</option>
        </select>
        <select v-model="resultFilters.reviewStatus" class="input" @change="loadResults">
          <option value="">Any review status</option>
          <option value="open">Open</option>
          <option value="cleared">Cleared</option>
          <option value="redisclosed">Redisclosed</option>
        </select>
        <select v-model.number="resultFilters.days" class="input" @change="loadResults">
          <option :value="1">Today</option>
          <option :value="7">Last 7 days</option>
          <option :value="30">Last 30 days</option>
          <option :value="90">Last 90 days</option>
        </select>
      </div>

      <div class="card">
        <table class="grid">
          <thead>
            <tr>
              <th>Processed</th><th>Loan #</th><th>Borrower</th><th>Profile</th><th>Decision</th>
              <th>Fail</th><th>Review</th><th>Summary</th><th>Review status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in filteredResults" :key="r.id" class="clickable" @click="openResult(r.id)">
              <td>{{ fmtDate(r.processedAtUtc) }}</td>
              <td>{{ r.loanNumber || '—' }}</td>
              <td>{{ r.borrowerName || '—' }}</td>
              <td class="muted">{{ [r.loanType, r.purposeGroup, r.occupancy, r.propertyState, r.amortization].filter(Boolean).join(' · ') }}</td>
              <td><span class="pill" :class="r.decision">{{ r.decision }}</span></td>
              <td class="num">{{ r.failCount }}</td>
              <td class="num">{{ r.reviewCount }}</td>
              <td class="summary-cell">{{ r.summary }}</td>
              <td><span v-if="r.reviewStatus" class="pill" :class="'rs-' + r.reviewStatus">{{ r.reviewStatus }}</span></td>
            </tr>
            <tr v-if="!filteredResults.length"><td colspan="9" class="empty">No results.</td></tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- ═══════════════════════ RULES ═══════════════════════ -->
    <section v-if="tab === 'rules'">
      <div class="subtabs">
        <button v-for="s in ruleTabs" :key="s.key" class="subtab" :class="{ active: ruleTab === s.key }" @click="ruleTab = s.key">{{ s.label }}</button>
      </div>

      <!-- ── Rules ── -->
      <div v-if="ruleTab === 'rules'">
        <div class="toolbar">
          <input v-model="ruleSearch" class="input grow" placeholder="Search form, group, wording..." />
          <select v-model="ruleGroupFilter" class="input">
            <option value="">All groups</option>
            <option v-for="g in ruleGroups" :key="g" :value="g">{{ g }}</option>
          </select>
          <select v-model="ruleStateFilter" class="input">
            <option value="">All rules</option>
            <option value="__state__">State-specific only</option>
            <option value="__federal__">Federal (no state) only</option>
            <option v-for="s in STATES" :key="s" :value="s">Applies in {{ s }}</option>
          </select>
          <select v-model="ruleSourceFilter" class="input">
            <option value="">All sources</option>
            <option value="seed">Word doc (seed)</option>
            <option value="vendor">Vendor master list</option>
            <option value="manual">Manual</option>
          </select>
          <label class="check"><input type="checkbox" v-model="showInactive" /> Show inactive</label>
          <button class="btn primary" @click="editRule(null)"><i class="fa-solid fa-plus"></i> Add rule</button>
        </div>
        <div class="card">
          <table class="grid">
            <thead>
              <tr><th>Group</th><th>Form</th><th>Requirement</th><th>Loan type</th><th>Purpose</th><th>Occupancy</th>
                  <th>States</th><th>Amort.</th><th>Condition</th><th>Source</th><th></th></tr>
            </thead>
            <tbody>
              <tr v-for="r in filteredRules" :key="r.id" :class="{ inactive: !r.isActive }">
                <td>{{ r.ruleGroup }}</td>
                <td><b>{{ formName(r.formCode) }}</b><div class="muted small">{{ r.formCode }}</div></td>
                <td><span class="pill" :class="'req-' + r.requirement.toLowerCase()">{{ r.requirement }}</span></td>
                <td>{{ r.loanTypes || 'Any' }}</td>
                <td>{{ r.purposes || 'Any' }}</td>
                <td>
                  <span v-if="r.occupancies">{{ r.occupancies }}</span>
                  <span v-if="r.excludeOccupancies" class="muted"> not {{ r.excludeOccupancies }}</span>
                  <span v-if="!r.occupancies && !r.excludeOccupancies">Any</span>
                </td>
                <td>
                  <span v-if="r.includeStates" class="state-list">{{ r.includeStates }}</span>
                  <span v-if="r.excludeStates" class="muted"> except {{ r.excludeStates }}</span>
                  <span v-if="!r.includeStates && !r.excludeStates" class="muted">All</span>
                </td>
                <td>{{ r.amortizations || 'Any' }}</td>
                <td>{{ r.subConditionCode || '' }}</td>
                <td><span class="pill" :class="'src-' + r.source">{{ r.source }}</span></td>
                <td class="actions">
                  <button class="icon-btn" title="Edit" @click="editRule(r)"><i class="fa-solid fa-pen"></i></button>
                  <button class="icon-btn danger" title="Delete" @click="deleteRow('rule', r.id, `rule #${r.id} (${r.formCode})`)"><i class="fa-solid fa-trash"></i></button>
                </td>
              </tr>
              <tr v-if="!filteredRules.length"><td colspan="11" class="empty">No rules match.</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Forms ── -->
      <div v-if="ruleTab === 'forms'">
        <div class="toolbar">
          <input v-model="formSearch" class="input grow" placeholder="Search forms..." />
          <label class="check"><input type="checkbox" v-model="showInactive" /> Show inactive</label>
          <button class="btn primary" @click="editForm(null)"><i class="fa-solid fa-plus"></i> Add disclosure</button>
        </div>
        <div class="card">
          <table class="grid">
            <thead><tr><th>Code</th><th>Name</th><th>Per borrower</th><th>Rules</th><th>Aliases</th><th>Notes</th><th>Active</th><th></th></tr></thead>
            <tbody>
              <tr v-for="f in filteredForms" :key="f.formCode" :class="{ inactive: !f.isActive }">
                <td class="mono">{{ f.formCode }}</td>
                <td><b>{{ f.displayName }}</b></td>
                <td>{{ f.perBorrower ? 'Yes' : '' }}</td>
                <td class="num">{{ rulesFor(f.formCode).length }}</td>
                <td class="num">{{ aliasesFor(f.formCode).length }}</td>
                <td class="muted">{{ f.notes }}</td>
                <td>{{ f.isActive ? 'Yes' : 'No' }}</td>
                <td class="actions">
                  <button class="icon-btn" title="Edit" @click="editForm(f)"><i class="fa-solid fa-pen"></i></button>
                  <button class="icon-btn" title="Aliases" @click="aliasFormFilter = f.formCode; ruleTab = 'aliases'"><i class="fa-solid fa-tags"></i></button>
                  <button class="icon-btn danger" title="Delete" @click="deleteForm(f)"><i class="fa-solid fa-trash"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Aliases ── -->
      <div v-if="ruleTab === 'aliases'">
        <div class="card test-box">
          <div class="section-title">Test a form name</div>
          <div class="toolbar">
            <input v-model="testFormName" class="input grow" placeholder="Paste a formName from a disclosure log, e.g. Affiliated Business Disclosure - Radiant" @keyup.enter="testMatch" />
            <button class="btn" @click="testMatch">Test</button>
          </div>
          <div v-if="testResult" class="test-result">
            Normalized: <span class="mono">{{ testResult.normalizedName }}</span> →
            <b v-if="testResult.matchedCode">{{ testResult.matchedCode }}</b><b v-else class="bad">no match</b>
            <span v-if="testResult.matchedAliasId"> (alias #{{ testResult.matchedAliasId }})</span>
            <span v-if="testResult.tiedCodes && testResult.tiedCodes.length > 1" class="bad"> — tie: {{ testResult.tiedCodes.join(', ') }}</span>
            <button v-if="!testResult.matchedCode" class="btn small" @click="editAlias({ matchType: 'exact', pattern: testResult.normalizedName })">Add alias</button>
          </div>
        </div>
        <div class="toolbar">
          <select v-model="aliasFormFilter" class="input">
            <option value="">All forms</option>
            <option v-for="f in catalog" :key="f.formCode" :value="f.formCode">{{ f.displayName }}</option>
          </select>
          <label class="check"><input type="checkbox" v-model="showInactive" /> Show inactive</label>
          <div class="grow"></div>
          <button class="btn primary" @click="editAlias(null)"><i class="fa-solid fa-plus"></i> Add alias</button>
        </div>
        <div class="card">
          <table class="grid">
            <thead><tr><th>Form</th><th>Match</th><th>Pattern</th><th>Priority</th><th>Source</th><th>Active</th><th></th></tr></thead>
            <tbody>
              <tr v-for="a in filteredAliases" :key="a.id" :class="{ inactive: !a.isActive }">
                <td>{{ formName(a.formCode) }}</td>
                <td>{{ a.matchType }}</td>
                <td class="mono">{{ a.pattern }}</td>
                <td class="num">{{ a.priority }}</td>
                <td class="muted">{{ a.source }}</td>
                <td>{{ a.isActive ? 'Yes' : 'No' }}</td>
                <td class="actions">
                  <button class="icon-btn" @click="editAlias(a)"><i class="fa-solid fa-pen"></i></button>
                  <button class="icon-btn danger" @click="deleteRow('alias', a.id, `alias '${a.pattern}'`)"><i class="fa-solid fa-trash"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="help">Patterns are compared against the <b>normalized</b> form name: lowercase, &amp; → "and", apostrophes and periods removed, other punctuation → space.
          Highest priority wins; two different forms tying at the top priority shows up as an "overlap" finding.</p>
      </div>

      <!-- ── Field conditions ── -->
      <div v-if="ruleTab === 'conditions'">
        <div class="toolbar">
          <p class="help grow">Conditions let a rule depend on an Encompass field (e.g. VA IRRRL, active military). Rows with the same code are OR-ed.
            A row that isn't <b>verified</b> evaluates to "unknown", which can only raise a <i>review</i> — never fail a package.</p>
          <button class="btn primary" @click="editCondition(null)"><i class="fa-solid fa-plus"></i> Add condition</button>
        </div>
        <div class="card">
          <table class="grid">
            <thead><tr><th>Code</th><th>Field</th><th>Operator</th><th>Value</th><th>Verified</th><th>Notes</th><th>Used by</th><th></th></tr></thead>
            <tbody>
              <tr v-for="c in subConditions" :key="c.id" :class="{ inactive: !c.isActive }">
                <td class="mono">{{ c.code }}</td>
                <td class="mono">{{ c.fieldId || '— not wired —' }}</td>
                <td>{{ c.operator }}</td>
                <td class="mono">{{ c.value }}</td>
                <td><span class="pill" :class="c.isVerified ? 'pass' : 'review'">{{ c.isVerified ? 'Yes' : 'No' }}</span></td>
                <td class="muted">{{ c.notes }}</td>
                <td class="num">{{ rules.filter(r => r.subConditionCode === c.code).length }}</td>
                <td class="actions">
                  <button class="icon-btn" @click="editCondition(c)"><i class="fa-solid fa-pen"></i></button>
                  <button class="icon-btn danger" @click="deleteRow('subcondition', c.id, `condition ${c.code}`)"><i class="fa-solid fa-trash"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Vendor master list ── -->
      <div v-if="ruleTab === 'vendor'">
        <p class="help">The document vendor's full disclosure inventory (Disclosure_List.xlsx). Every name here is an alias, and state forms the vendor
          prints in initial packages are rules (Source = vendor). They're only used when <b>UseVendorRules</b> is on in Settings.
          <span v-if="vendorFlag === false" class="pill warn">UseVendorRules is OFF</span></p>
        <div class="toolbar">
          <select v-model="vendor.territory" class="input" @change="loadVendor">
            <option value="">All territories</option>
            <option value="FED">FED</option><option value="FHA">FHA</option><option value="V.A.">VA (program)</option><option value="RET">Retired</option>
            <option v-for="st in STATES" :key="st" :value="st">{{ st }}</option>
          </select>
          <input v-model="vendor.search" class="input grow" placeholder="Search name or print condition..." @keyup.enter="loadVendor" />
          <label class="check"><input type="checkbox" v-model="vendor.initialOnly" @change="loadVendor" /> Initial packages only</label>
          <button class="btn" @click="loadVendor">Search</button>
        </div>
        <div class="card">
          <table class="grid">
            <thead><tr><th>Territory</th><th>Disclosure</th><th>App</th><th>3-day</th><th>LE</th><th>Sign</th><th>Print condition</th><th>Mapped to</th></tr></thead>
            <tbody>
              <tr v-for="v in vendor.rows" :key="v.DisclosureId">
                <td class="mono">{{ v.TerritoryCode }}</td>
                <td><b>{{ v.DisclosureName }}</b><div class="muted small">#{{ v.DisclosureId }} {{ v.Identifier }}</div></td>
                <td>{{ v.LenderApplication ? 'Yes' : '' }}</td>
                <td>{{ v.LenderThreeDay ? 'Yes' : '' }}</td>
                <td>{{ v.LenderLE ? 'Yes' : '' }}</td>
                <td class="muted">{{ v.SignatureTypes }}</td>
                <td class="muted small summary-cell">{{ v.PrintCondition }}</td>
                <td class="mono small">{{ v.MappedFormCode }}</td>
              </tr>
              <tr v-if="!vendor.rows.length"><td colspan="8" class="empty">No vendor forms loaded (run DisclosureAuditVendorSeed.sql).</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Preview ── -->
      <div v-if="ruleTab === 'preview'">
        <div class="toolbar">
          <select v-model="preview.loanType" class="input"><option v-for="v in vocab.loanTypes" :key="v" :value="v">{{ v }}</option></select>
          <select v-model="preview.purpose" class="input"><option v-for="v in vocab.purposes" :key="v" :value="v">{{ v }}</option></select>
          <select v-model="preview.occupancy" class="input"><option v-for="v in vocab.occupancies" :key="v" :value="v">{{ v }}</option></select>
          <select v-model="preview.state" class="input"><option v-for="s in STATES" :key="s" :value="s">{{ s }}</option></select>
          <select v-model="preview.amortization" class="input"><option v-for="v in vocab.amortizations" :key="v" :value="v">{{ v }}</option></select>
          <button class="btn primary" @click="runPreview">Show required forms</button>
        </div>
        <div class="card" v-if="previewRows.length">
          <table class="grid">
            <thead><tr><th>Form</th><th>Status</th><th>Group</th><th>Why</th></tr></thead>
            <tbody>
              <tr v-for="p in previewRows" :key="p.formCode">
                <td><b>{{ p.displayName }}</b> <span v-if="p.stateSpecific" class="pill state">state</span></td>
                <td><span class="pill" :class="'pv-' + p.status.toLowerCase()">{{ p.status }}</span></td>
                <td>{{ p.ruleGroup }}</td>
                <td class="muted">{{ p.why }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── History ── -->
      <div v-if="ruleTab === 'history'">
        <div class="card">
          <table class="grid">
            <thead><tr><th>When</th><th>Who</th><th>Table</th><th>Row</th><th>Action</th><th>Change</th></tr></thead>
            <tbody>
              <tr v-for="h in history" :key="h.Id || h.id">
                <td>{{ fmtDate(h.ChangedAtUtc || h.changedAtUtc) }}</td>
                <td>{{ h.ChangedBy || h.changedBy }}</td>
                <td>{{ h.TableName || h.tableName }}</td>
                <td class="mono">{{ h.RowKey || h.rowKey }}</td>
                <td>{{ h.Action || h.action }}</td>
                <td class="mono small change-cell">{{ h.AfterJson || h.afterJson || h.BeforeJson || h.beforeJson }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════ SETTINGS ═══════════════════════ -->
    <section v-if="tab === 'settings'">
      <div class="card">
        <table class="grid">
          <thead><tr><th>Setting</th><th>Value</th><th>Default (appsettings)</th><th></th></tr></thead>
          <tbody>
            <tr v-for="s in settings" :key="s.key">
              <td><b>{{ s.key }}</b><div class="muted small">{{ SETTING_HELP[s.key] || '' }}</div></td>
              <td>
                <label v-if="s.type === 'bool'" class="switch">
                  <input type="checkbox" :checked="s.edit === 'true'" @change="s.edit = $event.target.checked ? 'true' : 'false'" />
                  <span>{{ s.edit === 'true' ? 'On' : 'Off' }}</span>
                </label>
                <input v-else v-model="s.edit" class="input" :type="s.type === 'int' ? 'number' : 'text'" :placeholder="s.type === 'list' ? 'comma separated' : ''" />
              </td>
              <td class="muted mono">{{ s.defaultValue }}</td>
              <td class="actions">
                <button class="btn small primary" :disabled="s.edit === s.value" @click="saveSetting(s)">Save</button>
                <button v-if="s.overridden" class="btn small" @click="resetSetting(s)">Use default</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="help">Changes take effect within 30 seconds. Connection strings and Encompass credentials aren't editable here.</p>
    </section>

    <!-- ═══════════════════════ SUGGESTIONS ═══════════════════════ -->
    <section v-if="tab === 'suggestions'">
      <p class="help">When model fallback is on, the model may say an unrecognized form name is a missing disclosure. Nothing changes until you approve — approving adds an exact alias.</p>
      <div class="card">
        <table class="grid">
          <thead><tr><th>Form name in package</th><th>Suggested disclosure</th><th>Confidence</th><th>Reason</th><th>Result</th><th></th></tr></thead>
          <tbody>
            <tr v-for="s in suggestions" :key="s.id">
              <td>{{ s.formName }}</td>
              <td><b>{{ formName(s.suggestedFormCode) }}</b></td>
              <td>{{ s.confidence }}</td>
              <td class="muted">{{ s.reason }}</td>
              <td><a v-if="s.resultId" href="#" @click.prevent="openResult(s.resultId)">#{{ s.resultId }}</a></td>
              <td class="actions">
                <button class="btn small primary" @click="decideSuggestion(s, true)">Approve</button>
                <button class="btn small" @click="decideSuggestion(s, false)">Reject</button>
              </td>
            </tr>
            <tr v-if="!suggestions.length"><td colspan="6" class="empty">No pending suggestions.</td></tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- ═══════════════════════ RESULT DETAIL ═══════════════════════ -->
    <div v-if="detail" class="modal-backdrop" @click.self="detail = null">
      <div class="modal wide">
        <div class="modal-head">
          <div>
            <h2>{{ detail.result.loanNumber || detail.result.loanId }} — {{ detail.result.borrowerName }}</h2>
            <div class="muted">
              {{ [detail.result.loanType, detail.result.purposeGroup, detail.result.occupancy, detail.result.propertyState, detail.result.amortization].filter(Boolean).join(' · ') }}
              · {{ detail.result.loanProgram }} · disclosed {{ fmtDate(detail.result.disclosedDateUtc) }} by {{ detail.result.disclosedBy }}
            </div>
          </div>
          <span class="pill big" :class="detail.result.decision">{{ detail.result.decision }}</span>
        </div>

        <div class="findings">
          <div v-for="sev in ['fail', 'review', 'info']" :key="sev">
            <template v-if="detail.findings.filter(f => f.severity === sev).length">
              <div class="sev-head" :class="sev">{{ SEV_LABEL[sev] }}</div>
              <div v-for="f in detail.findings.filter(x => x.severity === sev)" :key="f.id" class="finding">
                <span class="kind">{{ KIND_LABEL[f.kind] || f.kind }}</span>
                <b v-if="f.formCode">{{ formName(f.formCode) }}</b>
                <span v-if="f.formName" class="mono small"> "{{ f.formName }}"</span>
                <div class="muted small">{{ f.detail }}</div>
              </div>
            </template>
          </div>
        </div>

        <details>
          <summary>Package forms ({{ detail.forms.length }})</summary>
          <table class="grid compact">
            <thead><tr><th>Form name</th><th>Package</th><th>Type</th><th>Matched</th></tr></thead>
            <tbody>
              <tr v-for="f in detail.forms" :key="f.id">
                <td>{{ f.formName }}</td><td class="muted">{{ f.sourcePackage }}</td><td class="muted">{{ f.formType }}</td>
                <td><span v-if="f.matchedCode" class="mono">{{ f.matchedCode }}</span><span v-else class="bad">unrecognized</span></td>
              </tr>
            </tbody>
          </table>
        </details>
        <details>
          <summary>Debug notes</summary>
          <pre class="debug">{{ debugText(detail.result.debugNotes) }}</pre>
        </details>

        <div class="review-box">
          <div v-if="detail.result.reviewedBy" class="muted small">
            Last review: {{ detail.result.reviewStatus }} by {{ detail.result.reviewedBy }} {{ fmtDate(detail.result.reviewedAtUtc) }} — {{ detail.result.reviewNote }}
          </div>
          <textarea v-model="reviewNote" class="input" rows="2" placeholder="Note (what was fixed / why it's OK)"></textarea>
          <div class="modal-actions">
            <button class="btn" @click="recheck(detail.result.loanId)" :disabled="busy">Re-check now (dry run)</button>
            <div class="grow"></div>
            <button class="btn" @click="setReview('open')" :disabled="busy">Reopen</button>
            <button class="btn" @click="setReview('redisclosed')" :disabled="busy">Redisclosed</button>
            <button class="btn primary" @click="setReview('cleared')" :disabled="busy">Clear</button>
            <button class="btn" @click="detail = null">Close</button>
          </div>
          <div v-if="recheckResult" class="recheck">
            Dry-run result: <span class="pill" :class="recheckResult.decision">{{ recheckResult.decision }}</span>
            {{ recheckResult.outcome ? recheckResult.outcome.summary : recheckResult.message }}
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════ EDIT MODALS ═══════════════════════ -->
    <div v-if="editing" class="modal-backdrop" @click.self="editing = null">
      <div class="modal">
        <h2>{{ editing.isNew ? 'Add' : 'Edit' }} {{ EDIT_TITLE[editing.kind] }}</h2>

        <!-- Form -->
        <template v-if="editing.kind === 'form'">
          <label>Code<input v-model="editing.row.formCode" class="input mono" :disabled="!editing.isNew" placeholder="e.g. TN_RIGHT_TO_CHOOSE_INS" /></label>
          <label>Name<input v-model="editing.row.displayName" class="input" /></label>
          <label>Notes<input v-model="editing.row.notes" class="input" /></label>
          <label class="check"><input type="checkbox" v-model="editing.row.perBorrower" /> Expect one copy per borrower</label>
          <label class="check"><input type="checkbox" v-model="editing.row.isActive" /> Active</label>
          <template v-if="editing.isNew">
            <label>First alias (form name as it appears in Encompass)<input v-model="editing.firstAlias" class="input" placeholder="optional" /></label>
            <label class="check"><input type="checkbox" v-model="editing.addRequiredRule" /> Also add a "Required" rule</label>
          </template>
        </template>

        <!-- Rule -->
        <template v-if="editing.kind === 'rule'">
          <div class="two-col wide-left">
            <label>Form
              <select v-model="editing.row.formCode" class="input">
                <option v-for="f in catalog" :key="f.formCode" :value="f.formCode">{{ f.displayName }}</option>
              </select>
            </label>
            <label>Requirement
              <select v-model="editing.row.requirement" class="input">
                <option v-for="r in vocab.requirements" :key="r" :value="r">{{ r }}</option>
              </select>
            </label>
          </div>
          <label>Group<input v-model="editing.row.ruleGroup" class="input" list="da-groups" placeholder="e.g. Standard, FHA Purchase, TN State" /></label>
          <datalist id="da-groups"><option v-for="g in ruleGroups" :key="g" :value="g" /></datalist>

          <div class="cond-grid">
            <div><div class="cond-label">Loan types <span class="muted">(none = any)</span></div>
              <label v-for="v in vocab.loanTypes" :key="v" class="chip"><input type="checkbox" :value="v" v-model="editing.lists.loanTypes" />{{ v }}</label></div>
            <div><div class="cond-label">Purposes</div>
              <label v-for="v in vocab.purposes" :key="v" class="chip"><input type="checkbox" :value="v" v-model="editing.lists.purposes" />{{ v }}</label></div>
            <div><div class="cond-label">Only occupancies</div>
              <label v-for="v in vocab.occupancies" :key="v" class="chip"><input type="checkbox" :value="v" v-model="editing.lists.occupancies" />{{ v }}</label></div>
            <div><div class="cond-label">Exclude occupancies</div>
              <label v-for="v in vocab.occupancies" :key="v" class="chip"><input type="checkbox" :value="v" v-model="editing.lists.excludeOccupancies" />{{ v }}</label></div>
            <div><div class="cond-label">Amortization</div>
              <label v-for="v in vocab.amortizations" :key="v" class="chip"><input type="checkbox" :value="v" v-model="editing.lists.amortizations" />{{ v }}</label></div>
            <div><div class="cond-label">Field condition</div>
              <select v-model="editing.row.subConditionCode" class="input">
                <option :value="null">— none —</option>
                <option v-for="c in conditionCodes" :key="c" :value="c">{{ c }}</option>
              </select></div>
          </div>

          <div class="cond-label">States</div>
          <div class="state-mode">
            <label class="check"><input type="radio" value="all" v-model="editing.stateMode" /> All states</label>
            <label class="check"><input type="radio" value="include" v-model="editing.stateMode" /> Only these states</label>
            <label class="check"><input type="radio" value="exclude" v-model="editing.stateMode" /> All except these</label>
          </div>
          <div v-if="editing.stateMode !== 'all'" class="state-picker">
            <label v-for="s in STATES" :key="s" class="state-chip" :class="{ on: editing.states.includes(s) }">
              <input type="checkbox" :value="s" v-model="editing.states" />{{ s }}
            </label>
          </div>

          <label>Wording from the disclosure list<input v-model="editing.row.sourceText" class="input" /></label>
          <label class="check"><input type="checkbox" v-model="editing.row.isActive" /> Active</label>
        </template>

        <!-- Alias -->
        <template v-if="editing.kind === 'alias'">
          <label>Form
            <select v-model="editing.row.formCode" class="input">
              <option v-for="f in catalog" :key="f.formCode" :value="f.formCode">{{ f.displayName }}</option>
            </select>
          </label>
          <div class="two-col">
            <label>Match type
              <select v-model="editing.row.matchType" class="input"><option v-for="m in vocab.matchTypes" :key="m" :value="m">{{ m }}</option></select>
            </label>
            <label>Priority<input v-model.number="editing.row.priority" type="number" class="input" /></label>
          </div>
          <label>Pattern<input v-model="editing.row.pattern" class="input mono" /></label>
          <label class="check"><input type="checkbox" v-model="editing.row.isActive" /> Active</label>
        </template>

        <!-- Condition -->
        <template v-if="editing.kind === 'subcondition'">
          <label>Code<input v-model="editing.row.code" class="input mono" list="da-conds" placeholder="e.g. VaIrrrl" /></label>
          <datalist id="da-conds"><option v-for="c in conditionCodes" :key="c" :value="c" /></datalist>
          <div class="two-col">
            <label>Encompass field ID<input v-model="editing.row.fieldId" class="input mono" /></label>
            <label>Operator
              <select v-model="editing.row.operator" class="input"><option v-for="o in vocab.operators" :key="o" :value="o">{{ o }}</option></select>
            </label>
          </div>
          <label>Value<input v-model="editing.row.value" class="input mono" :disabled="['blank','notblank'].includes(editing.row.operator)" /></label>
          <label>Notes<input v-model="editing.row.notes" class="input" /></label>
          <label class="check"><input type="checkbox" v-model="editing.row.isVerified" /> Verified (field confirmed on real loans)</label>
          <label class="check"><input type="checkbox" v-model="editing.row.isActive" /> Active</label>
        </template>

        <div v-if="editError" class="error-banner">{{ editError }}</div>
        <div class="modal-actions">
          <div class="grow"></div>
          <button class="btn" @click="editing = null">Cancel</button>
          <button class="btn primary" @click="saveEdit" :disabled="busy">Save</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeaderPortal from "@/views/PageHeaderPortal.vue"
import api from '@/api/api.service'
import auth from '@/authConfig.js'

const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA',
  'MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX',
  'UT','VT','VA','WA','WV','WI','WY']

const csvToList = s => (s || '').split(',').map(x => x.trim()).filter(Boolean)
const listToCsv = l => (l && l.length ? l.join(',') : null)

export default {
  name: 'DisclosureAuditDashboard',
  components: { PageHeaderPortal },
  data() {
    return {
      STATES,
      SEV_LABEL: { fail: 'Must fix', review: 'Needs review', info: 'FYI' },
      KIND_LABEL: {
        missing: 'Missing', missing_pending: 'Not sent yet', prohibited_present: 'Should not be sent', possible_match: 'Possible match',
        verify_conditional: 'Verify', count_short: 'Copies short', overlap: 'Ambiguous match',
        contents_mismatch: 'Contents mismatch', package_anomaly: 'Package', fact_missing: 'Missing loan data',
        not_applicable: 'Not required here', expected_missing: 'Vendor would print', known_extra: 'Not on list', unrecognized: 'Unrecognized'
      },
      EDIT_TITLE: { form: 'disclosure', rule: 'rule', alias: 'alias', subcondition: 'field condition' },
      SETTING_HELP: {
        Enabled: 'Master switch. Off = the timer job does nothing (dry runs still work).',
        UseVendorRules: 'Use the vendor master list: exact aliases for all 543 vendor forms + state rules for initial packages.',
        UseModelFallback: 'Ask the model about unrecognized form names when something is missing (review only, never passes a package).',
        QueueBatchSize: 'Loans per timer run.',
        MinQueueAgeMinutes: 'Wait this long after the webhook before reading the disclosure log.',
        MaxAttempts: 'Retries before a queue row is parked as error.',
        MaxPackageAgeDays: 'Skip webhooks that arrive this many days after the initial LE (they belong to later packages).',
        BackfillFromWebhookQueue: 'Copy disclosureCreation webhooks that never reached the audit queue.',
        BackfillLookbackDays: 'How far back the backfill looks.',
        ExcludedChannels: 'Channels (2626) never audited, e.g. Brokered.',
        TextModel: 'Ollama model for the fallback.'
      },
      tabs: [
        { key: 'results', label: 'Results', icon: 'fa-duotone fa-list-check' },
        { key: 'rules', label: 'Rules', icon: 'fa-duotone fa-scale-balanced' },
        { key: 'settings', label: 'Settings', icon: 'fa-duotone fa-sliders' },
        { key: 'suggestions', label: 'Alias suggestions', icon: 'fa-duotone fa-wand-magic-sparkles' }
      ],
      ruleTabs: [
        { key: 'rules', label: 'Rules' },
        { key: 'forms', label: 'Disclosures' },
        { key: 'aliases', label: 'Form name aliases' },
        { key: 'conditions', label: 'Field conditions' },
        { key: 'vendor', label: 'Vendor master list' },
        { key: 'preview', label: 'Preview by state / profile' },
        { key: 'history', label: 'Change history' }
      ],
      tab: 'results',
      ruleTab: 'rules',
      loading: false,
      busy: false,
      errorMessage: '',
      flash: '',
      // results
      results: [],
      queueStatus: null,
      search: '',
      resultFilters: { decision: '', reviewStatus: 'open', days: 30 },
      detail: null,
      reviewNote: '',
      recheckResult: null,
      // rules
      catalog: [], aliases: [], rules: [], subConditions: [],
      vocab: { matchTypes: [], requirements: [], operators: [], loanTypes: [], purposes: [], occupancies: [], amortizations: [] },
      showInactive: false,
      ruleSearch: '', ruleGroupFilter: '', ruleStateFilter: '', ruleSourceFilter: '',
      vendor: { territory: 'TN', search: '', initialOnly: true, rows: [] },
      vendorFlag: null,
      formSearch: '', aliasFormFilter: '',
      testFormName: '', testResult: null,
      preview: { loanType: 'FHA', purpose: 'Purchase', occupancy: 'Primary', state: 'TN', amortization: 'Fixed' },
      previewRows: [],
      history: [],
      // settings / suggestions
      settings: [],
      enabledFlag: null,
      suggestions: [],
      // editing
      editing: null,
      editError: ''
    }
  },
  computed: {
    filteredResults() {
      const t = this.search.trim().toLowerCase()
      if (!t) return this.results
      return this.results.filter(r => [r.loanNumber, r.borrowerName, r.loanOfficer, r.summary, r.loanId]
        .filter(Boolean).join(' ').toLowerCase().includes(t))
    },
    ruleGroups() { return [...new Set(this.rules.map(r => r.ruleGroup))].sort() },
    conditionCodes() { return [...new Set(this.subConditions.map(c => c.code))].sort() },
    filteredRules() {
      const t = this.ruleSearch.trim().toLowerCase()
      const st = this.ruleStateFilter
      return this.rules.filter(r => {
        if (!this.showInactive && !r.isActive) return false
        if (this.ruleGroupFilter && r.ruleGroup !== this.ruleGroupFilter) return false
        if (this.ruleSourceFilter && r.source !== this.ruleSourceFilter) return false
        if (st === '__state__' && !r.includeStates && !r.excludeStates) return false
        if (st === '__federal__' && (r.includeStates || r.excludeStates)) return false
        if (st && st.length === 2) {
          const inc = csvToList(r.includeStates), exc = csvToList(r.excludeStates)
          if (inc.length && !inc.includes(st)) return false
          if (exc.includes(st)) return false
        }
        if (t && ![r.ruleGroup, r.formCode, this.formName(r.formCode), r.sourceText].filter(Boolean).join(' ').toLowerCase().includes(t)) return false
        return true
      })
    },
    filteredForms() {
      const t = this.formSearch.trim().toLowerCase()
      return this.catalog.filter(f => (this.showInactive || f.isActive) &&
        (!t || [f.formCode, f.displayName, f.notes].filter(Boolean).join(' ').toLowerCase().includes(t)))
    },
    filteredAliases() {
      return this.aliases.filter(a => (this.showInactive || a.isActive) && (!this.aliasFormFilter || a.formCode === this.aliasFormFilter))
    }
  },
  watch: {
    ruleTab(v) {
      if (v === 'vendor' && !this.vendor.rows.length) this.loadVendor()
      if (v === 'history') this.loadHistory()
    }
  },
  async mounted() {
    await Promise.all([this.loadResults(), this.loadRules(), this.loadSettings(), this.loadSuggestions()])
  },
  methods: {
    // ── API plumbing ─────────────────────────────────────────────────────
    async token() {
      const r = await auth.acquireTokenResponse()
      return r.accessToken
    },
    async get(path, params) {
      const res = await api.DisclosureAuditGet(path, params, await this.token())
      return res.data
    },
    async post(path, body) {
      const res = await api.DisclosureAuditPost(path, body || {}, await this.token())
      return res.data
    },
    errText(e, fallback) {
      return e?.response?.data?.error || e?.response?.data?.message || e?.message || fallback
    },
    showFlash(msg) {
      this.flash = msg
      setTimeout(() => { if (this.flash === msg) this.flash = '' }, 3500)
    },
    async run(fn, fallback) {
      this.errorMessage = ''
      try { return await fn() } catch (e) { this.errorMessage = this.errText(e, fallback); return null }
    },

    switchTab(key) {
      this.tab = key
      if (key === 'rules' && this.ruleTab === 'history') this.loadHistory()
    },
    async refreshTab() {
      this.loading = true
      try {
        if (this.tab === 'results') await this.loadResults()
        if (this.tab === 'rules') { await this.loadRules(); if (this.ruleTab === 'history') await this.loadHistory() }
        if (this.tab === 'settings') await this.loadSettings()
        if (this.tab === 'suggestions') await this.loadSuggestions()
      } finally { this.loading = false }
    },

    // ── Results ──────────────────────────────────────────────────────────
    async loadResults() {
      await this.run(async () => {
        const p = { top: 500, days: this.resultFilters.days }
        if (this.resultFilters.decision) p.decision = this.resultFilters.decision
        if (this.resultFilters.reviewStatus) p.reviewStatus = this.resultFilters.reviewStatus
        const [res, q] = await Promise.all([this.get('results', p), this.get('queue-status')])
        this.results = res?.rows || []
        this.queueStatus = q?.status || null
      }, 'Failed to load results.')
    },
    async openResult(id) {
      this.reviewNote = ''
      this.recheckResult = null
      await this.run(async () => { this.detail = await this.get(`results/${id}`) }, 'Failed to load result.')
    },
    async setReview(status) {
      this.busy = true
      await this.run(async () => {
        await this.post(`results/${this.detail.result.id}/review`, { review_status: status, note: this.reviewNote })
        this.showFlash(`Marked ${status}.`)
        const id = this.detail.result.id
        await this.loadResults()
        await this.openResult(id)
      }, 'Failed to save review.')
      this.busy = false
    },
    async recheck(loanId) {
      this.busy = true
      await this.run(async () => {
        const r = await this.post('process-loan', { loan_id: loanId, dry_run: true, force: true })
        this.recheckResult = r?.result || null
      }, 'Re-check failed.')
      this.busy = false
    },
    debugText(json) {
      try { return (JSON.parse(json || '[]') || []).join('\n') } catch { return json }
    },

    // ── Rules ────────────────────────────────────────────────────────────
    async loadRules() {
      await this.run(async () => {
        const r = await this.get('rules')
        this.catalog = r?.catalog || []
        this.aliases = r?.aliases || []
        this.rules = r?.rules || []
        this.subConditions = r?.subConditions || []
        if (r?.vocab) this.vocab = r.vocab
      }, 'Failed to load rules.')
    },
    async loadHistory() {
      await this.run(async () => { this.history = (await this.get('rules/changes', { top: 200 }))?.rows || [] }, 'Failed to load history.')
    },
    formName(code) { return (this.catalog.find(f => f.formCode === code) || {}).displayName || code },
    rulesFor(code) { return this.rules.filter(r => r.formCode === code && r.isActive) },
    aliasesFor(code) { return this.aliases.filter(a => a.formCode === code && a.isActive) },

    editForm(f) {
      this.editError = ''
      this.editing = {
        kind: 'form', isNew: !f,
        row: f ? { ...f } : { formCode: '', displayName: '', notes: '', perBorrower: false, isActive: true },
        firstAlias: '', addRequiredRule: true
      }
    },
    editRule(r) {
      this.editError = ''
      const row = r ? { ...r } : {
        id: 0, ruleGroup: this.ruleGroupFilter || '', formCode: this.catalog[0]?.formCode, requirement: 'Required',
        subConditionCode: null, sourceText: '', isActive: true
      }
      const inc = csvToList(row.includeStates), exc = csvToList(row.excludeStates)
      const presetState = !r && this.ruleStateFilter.length === 2 ? [this.ruleStateFilter] : []
      this.editing = {
        kind: 'rule', isNew: !r, row,
        lists: {
          loanTypes: csvToList(row.loanTypes), purposes: csvToList(row.purposes),
          occupancies: csvToList(row.occupancies), excludeOccupancies: csvToList(row.excludeOccupancies),
          amortizations: csvToList(row.amortizations)
        },
        stateMode: inc.length || presetState.length ? 'include' : exc.length ? 'exclude' : 'all',
        states: inc.length ? inc : exc.length ? exc : presetState
      }
    },
    editAlias(a) {
      this.editError = ''
      const isNew = !a || !a.id
      this.editing = {
        kind: 'alias', isNew,
        row: { id: 0, formCode: this.aliasFormFilter || this.catalog[0]?.formCode, matchType: 'contains', pattern: '', priority: 10, isActive: true, ...(a || {}) }
      }
    },
    editCondition(c) {
      this.editError = ''
      this.editing = {
        kind: 'subcondition', isNew: !c,
        row: c ? { ...c } : { id: 0, code: '', fieldId: '', operator: 'equals', value: '', isVerified: false, notes: '', isActive: true }
      }
    },
    async saveEdit() {
      const e = this.editing
      this.busy = true
      this.editError = ''
      try {
        if (e.kind === 'form') {
          const saved = await this.post('rules/forms', e.row)
          const code = saved?.row?.formCode || e.row.formCode
          if (e.isNew && e.firstAlias) await this.post('rules/aliases', { id: 0, formCode: code, matchType: 'exact', pattern: e.firstAlias, priority: 15, isActive: true })
          if (e.isNew && e.addRequiredRule) await this.post('rules/rules', { id: 0, formCode: code, ruleGroup: 'Standard', requirement: 'Required', isActive: true, sourceText: e.row.displayName })
        } else if (e.kind === 'rule') {
          const row = { ...e.row }
          Object.keys(e.lists).forEach(k => { row[k] = listToCsv(e.lists[k]) })
          row.includeStates = e.stateMode === 'include' ? listToCsv(e.states) : null
          row.excludeStates = e.stateMode === 'exclude' ? listToCsv(e.states) : null
          if (e.stateMode !== 'all' && !e.states.length) throw new Error('Pick at least one state, or choose "All states".')
          await this.post('rules/rules', row)
        } else if (e.kind === 'alias') {
          await this.post('rules/aliases', e.row)
        } else if (e.kind === 'subcondition') {
          await this.post('rules/subconditions', e.row)
        }
        this.editing = null
        this.showFlash('Saved.')
        await this.loadRules()
      } catch (err) {
        this.editError = this.errText(err, 'Save failed.')
      } finally {
        this.busy = false
      }
    },
    async deleteRow(kind, id, label) {
      if (!window.confirm(`Delete ${label}? This can't be undone (it's kept in change history).`)) return
      await this.run(async () => {
        await this.post(`rules/${kind}/${id}/delete`)
        this.showFlash('Deleted.')
        await this.loadRules()
      }, 'Delete failed.')
    },
    async deleteForm(f) {
      const n = this.rulesFor(f.formCode).length, a = this.aliasesFor(f.formCode).length
      if (!window.confirm(`Delete "${f.displayName}" and its ${n} rule(s) and ${a} alias(es)?\n\nTip: unchecking Active instead keeps it for later.`)) return
      await this.run(async () => {
        await this.post(`rules/forms/${encodeURIComponent(f.formCode)}/delete`)
        this.showFlash('Deleted.')
        await this.loadRules()
      }, 'Delete failed.')
    },
    async testMatch() {
      if (!this.testFormName.trim()) return
      await this.run(async () => { this.testResult = await this.get('rules/match-preview', { formName: this.testFormName }) }, 'Test failed.')
    },
    async runPreview() {
      await this.run(async () => { this.previewRows = (await this.get('rules/preview', this.preview))?.rows || [] }, 'Preview failed.')
    },

    async loadVendor() {
      await this.run(async () => {
        const p = { initialOnly: this.vendor.initialOnly, top: 800 }
        if (this.vendor.territory) p.territory = this.vendor.territory
        if (this.vendor.search.trim()) p.search = this.vendor.search.trim()
        this.vendor.rows = (await this.get('vendor-forms', p))?.rows || []
      }, 'Failed to load vendor list.')
    },

    // ── Settings ─────────────────────────────────────────────────────────
    async loadSettings() {
      await this.run(async () => {
        const rows = (await this.get('config'))?.rows || []
        this.settings = rows.map(r => ({ ...r, edit: r.value }))
        const en = rows.find(r => r.key === 'Enabled')
        this.enabledFlag = en ? en.value === 'true' : null
        const vf = rows.find(r => r.key === 'UseVendorRules')
        this.vendorFlag = vf ? vf.value === 'true' : null
      }, 'Failed to load settings.')
    },
    async saveSetting(s) {
      await this.run(async () => {
        await this.post('config', { key: s.key, value: String(s.edit ?? '') })
        this.showFlash(`${s.key} saved.`)
        await this.loadSettings()
      }, 'Save failed.')
    },
    async resetSetting(s) {
      await this.run(async () => {
        await this.post('config', { key: s.key, value: null })
        this.showFlash(`${s.key} reset to default.`)
        await this.loadSettings()
      }, 'Reset failed.')
    },

    // ── Suggestions ──────────────────────────────────────────────────────
    async loadSuggestions() {
      await this.run(async () => { this.suggestions = (await this.get('alias-suggestions'))?.rows || [] }, 'Failed to load suggestions.')
    },
    async decideSuggestion(s, approve) {
      await this.run(async () => {
        await this.post(`alias-suggestions/${s.id}/${approve ? 'approve' : 'reject'}`)
        this.showFlash(approve ? 'Alias added.' : 'Rejected.')
        await Promise.all([this.loadSuggestions(), this.loadRules()])
      }, 'Failed.')
    },

    fmtDate(v) {
      if (!v) return ''
      const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(v) ? v : v + 'Z')
      return isNaN(d) ? v : d.toLocaleString(undefined, { month: '2-digit', day: '2-digit', year: '2-digit', hour: 'numeric', minute: '2-digit' })
    }
  }
}
</script>

<style scoped>
.da-page { padding: 18px; background: #f7f8fb; min-height: calc(100vh - 90px); color: #1f2a44; }
.page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 12px; }
.page-header-left { display: flex; align-items: center; gap: 14px; }
.page-icon { width: 48px; height: 48px; border-radius: 5px; background: #123a71; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 20px; }
.page-title { margin: 0; font-size: 20px; font-weight: 700; }
.page-subtitle { margin: 4px 0 0; font-size: 13px; color: #7b8397; }
.header-actions { display: flex; gap: 8px; }

.tabs { display: flex; gap: 4px; border-bottom: 1px solid #dde4f0; margin-bottom: 12px; }
.tab { background: none; border: none; padding: 9px 14px; font-weight: 600; color: #6d7789; cursor: pointer; border-bottom: 2px solid transparent; font-size: 13px; }
.tab.active { color: #123a71; border-bottom-color: #123a71; }
.tab-badge { background: #b24553; color: #fff; border-radius: 999px; padding: 0 6px; font-size: 11px; margin-left: 4px; }
.subtabs { display: flex; gap: 6px; margin-bottom: 10px; flex-wrap: wrap; }
.subtab { border: 1px solid #dde4f0; background: #fff; border-radius: 999px; padding: 5px 12px; font-size: 12px; font-weight: 600; color: #4b5670; cursor: pointer; }
.subtab.active { background: #123a71; color: #fff; border-color: #123a71; }

.toolbar { display: flex; gap: 8px; align-items: center; margin-bottom: 10px; flex-wrap: wrap; }
.grow { flex: 1 1 auto; }
.spacer { flex: 1; }
.input { border: 1px solid #d5dbe6; border-radius: 5px; padding: 6px 9px; font-size: 13px; background: #fff; color: #1f2a44; }
.input.grow { min-width: 240px; }
.btn { border: 1px solid #d5dbe6; background: #fff; border-radius: 5px; padding: 6px 12px; font-size: 13px; font-weight: 600; cursor: pointer; color: #28344f; }
.btn.primary { background: #123a71; color: #fff; border-color: #123a71; }
.btn.small { padding: 3px 9px; font-size: 12px; }
.btn:disabled { opacity: .5; cursor: default; }
.icon-btn { background: none; border: none; color: #5f79c7; cursor: pointer; padding: 3px 6px; }
.icon-btn.danger { color: #b24553; }
.check { display: inline-flex; align-items: center; gap: 5px; font-size: 13px; }

.card { background: #fff; border: 1px solid #e7ebf2; border-radius: 5px; overflow-x: auto; margin-bottom: 10px; }
.grid { width: 100%; border-collapse: collapse; font-size: 13px; }
.grid th { text-align: left; background: #f3f5f9; color: #5b6580; font-weight: 700; padding: 8px 10px; white-space: nowrap; font-size: 12px; }
.grid td { padding: 7px 10px; border-top: 1px solid #eef1f6; vertical-align: top; }
.grid.compact td { padding: 4px 8px; }
.grid tr.clickable { cursor: pointer; }
.grid tr.clickable:hover td { background: #f7f9fd; }
.grid tr.inactive td { opacity: .5; }
.num { text-align: right; }
.actions { white-space: nowrap; text-align: right; }
.empty { text-align: center; color: #9aa3b5; padding: 20px; }
.muted { color: #7b8397; }
.small { font-size: 11px; }
.mono { font-family: Consolas, monospace; font-size: 12px; }
.bad { color: #b24553; }
.summary-cell { max-width: 460px; }
.change-cell { max-width: 420px; word-break: break-all; }
.state-list { font-weight: 600; }
.help { font-size: 12px; color: #7b8397; margin: 6px 0; }

.pill { display: inline-block; border-radius: 999px; padding: 2px 9px; font-size: 11px; font-weight: 700; background: #f2f4f7; color: #6d7789; border: 1px solid #e1e5ec; text-transform: capitalize; }
.pill.big { font-size: 14px; padding: 4px 14px; }
.pill.req-expected, .pill.pv-expected { background: #f3eefe; color: #6b46c1; border-color: #e3d8fb; }
.pill.src-vendor { background: #f3eefe; color: #6b46c1; border-color: #e3d8fb; text-transform: none; }
.pill.src-manual { background: #edf4ff; color: #3d6fd1; border-color: #d7e5fd; text-transform: none; }
.pill.src-seed { text-transform: none; }
.pill.pass, .pill.rs-cleared, .pill.req-optional, .pill.pv-optional { background: #eef9f0; color: #2c7a4b; border-color: #d9eedf; }
.pill.review, .pill.rs-open, .pill.pv-unknown { background: #fff7e6; color: #9a6b00; border-color: #f3e2b8; }
.pill.fail, .pill.error, .pill.req-prohibited, .pill.pv-prohibited { background: #fff0f1; color: #b24553; border-color: #f0d1d5; }
.pill.req-required, .pill.pv-required, .pill.rs-redisclosed { background: #edf4ff; color: #3d6fd1; border-color: #d7e5fd; }
.pill.state { background: #f3eefe; color: #6b46c1; border-color: #e3d8fb; text-transform: none; }
.pill.warn { background: #fff7e6; color: #9a6b00; border-color: #f3e2b8; }

.queue-status-bar { display: flex; align-items: center; gap: 10px; background: #f0f4fb; border: 1px solid #dde4f0; border-radius: 5px; padding: 8px 14px; margin-bottom: 12px; font-size: 13px; }
.queue-status-label { color: #5f79c7; font-weight: 700; display: flex; align-items: center; gap: 6px; }
.queue-pill { display: flex; align-items: center; gap: 5px; border-radius: 999px; padding: 3px 10px; font-size: 12px; font-weight: 700; border: 1px solid transparent; }
.queue-pill.pending { background: #f2f4f7; border-color: #e1e5ec; color: #6d7789; }
.queue-pill.processing { background: #edf4ff; border-color: #d7e5fd; color: #3d6fd1; }
.queue-pill.done { background: #eef9f0; border-color: #d9eedf; color: #2c7a4b; }
.queue-pill.errored { background: #fff0f1; border-color: #f0d1d5; color: #b24553; }
.q-count { font-size: 15px; font-weight: 800; }
.q-label { font-size: 11px; font-weight: 600; opacity: .75; }

.error-banner { background: #fff0f1; border: 1px solid #f0d1d5; color: #b24553; border-radius: 5px; padding: 8px 12px; margin-bottom: 10px; font-size: 13px; }
.flash-banner { background: #eef9f0; border: 1px solid #d9eedf; color: #2c7a4b; border-radius: 5px; padding: 8px 12px; margin-bottom: 10px; font-size: 13px; }

.test-box { padding: 12px; }
.section-title { font-size: 13px; font-weight: 700; margin-bottom: 8px; }
.test-result { font-size: 13px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }

.modal-backdrop { position: fixed; inset: 0; background: rgba(20, 30, 50, .45); display: flex; align-items: flex-start; justify-content: center; padding: 40px 16px; z-index: 2000; overflow-y: auto; }
.modal { background: #fff; border-radius: 6px; padding: 18px 20px; width: 640px; max-width: 100%; display: flex; flex-direction: column; gap: 10px; }
.modal.wide { width: 980px; }
.modal h2 { margin: 0; font-size: 17px; }
.modal label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; font-weight: 600; color: #4b5670; }
.modal label.check, .modal label.chip { flex-direction: row; align-items: center; font-weight: 500; }
.modal-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.modal-actions { display: flex; gap: 8px; align-items: center; margin-top: 4px; }
.two-col { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 10px; }
.two-col.wide-left { grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); }
/* grid/flex children default to min-width:auto, so a select with a long option label
   pushes past its column (and out of the modal) — let fields shrink to their column */
.two-col > *, .cond-grid > * { min-width: 0; }
.modal .input { width: 100%; box-sizing: border-box; min-width: 0; }
.modal select.input { text-overflow: ellipsis; }
.modal .check .input, .modal .chip .input { width: auto; }
.modal { box-sizing: border-box; overflow-x: hidden; }
.modal-actions .btn { white-space: nowrap; }
.cond-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 16px; background: #f7f8fb; border-radius: 5px; padding: 10px; }
.cond-label { font-size: 12px; font-weight: 700; color: #4b5670; margin-bottom: 4px; }
.chip { display: inline-flex; gap: 4px; margin-right: 10px; font-size: 12px; }
.state-mode { display: flex; gap: 14px; }
.state-picker { display: flex; flex-wrap: wrap; gap: 4px; }
.state-chip { flex-direction: row !important; border: 1px solid #d5dbe6; border-radius: 4px; padding: 2px 6px; font-size: 11px !important; cursor: pointer; font-family: Consolas, monospace; }
.state-chip input { display: none; }
.state-chip.on { background: #123a71; color: #fff; border-color: #123a71; }

.findings { display: flex; flex-direction: column; gap: 8px; }
.sev-head { font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; margin-top: 4px; }
.sev-head.fail { color: #b24553; }
.sev-head.review { color: #9a6b00; }
.sev-head.info { color: #7b8397; }
.finding { border-left: 3px solid #e1e5ec; padding: 4px 10px; font-size: 13px; }
.finding .kind { font-size: 11px; font-weight: 700; color: #5b6580; margin-right: 6px; text-transform: uppercase; }
details summary { cursor: pointer; font-size: 13px; font-weight: 600; color: #4b5670; margin: 4px 0; }
.debug { background: #f7f8fb; padding: 10px; font-size: 11px; white-space: pre-wrap; max-height: 260px; overflow: auto; }
.review-box { border-top: 1px solid #eef1f6; padding-top: 10px; display: flex; flex-direction: column; gap: 8px; }
.recheck { font-size: 13px; }
</style>
